import React, { useState, useCallback, useEffect } from 'react';
import IconButton from './IconButton';
import './DatePicker.css';

interface DatePickerProps {
  /** 現在選択されている日付 */
  selectedDate?: Date | null;
  /** 日付選択時のコールバック */
  onDateSelect?: (date: Date) => void;
  /** カレンダーを閉じる時のコールバック */
  onClose?: () => void;
  /** 表示する月（デフォルトは現在月または選択日の月） */
  displayMonth?: Date;
}

// 日付セルのタイプを定義
type DateCellType = 'thisMonth' | 'otherMonth' | 'selected' | 'today';

interface DateCellProps {
  date: Date;
  displayDate: number;
  type: DateCellType;
  onClick: (date: Date) => void;
}

const DateCell: React.FC<DateCellProps> = ({ 
  date, 
  displayDate, 
  type, 
  onClick 
}) => {
  const handleClick = () => {
    onClick(date);
  };

  const getClassName = () => {
    const baseClass = "date-picker__date-cell";
    const typeClass = type === 'otherMonth' ? 'other-month' : 
                     type === 'today' ? 'today' : 
                     type === 'selected' ? 'selected' : 'this-month';
    
    return `${baseClass} ${baseClass}--${typeClass}`;
  };

  return (
    <button
      type="button"
      className={getClassName()}
      onClick={handleClick}
    >
      <div className="date-picker__date-cell__container">
        <div className="date-picker__date-cell__state">
          <div className="date-picker__date-cell__content">
            <span className="date-picker__date-cell__text text-single-xlarge">
              {displayDate}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};

const DatePicker: React.FC<DatePickerProps> = ({
  selectedDate,
  onDateSelect,
  onClose,
  displayMonth: propDisplayMonth
}) => {
  // 表示する月の管理
  const [displayMonth, setDisplayMonth] = useState(() => {
    return propDisplayMonth || selectedDate || new Date();
  });

  // propDisplayMonthが変更された時に表示月を更新
  useEffect(() => {
    if (propDisplayMonth) {
      setDisplayMonth(propDisplayMonth);
    }
  }, [propDisplayMonth]);

  // 月の名前を取得
  const getMonthName = (date: Date): string => {
    return `${date.getFullYear()}年${date.getMonth() + 1}月`;
  };

  // 前月に移動
  const goToPreviousMonth = useCallback(() => {
    setDisplayMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  }, []);

  // 次月に移動
  const goToNextMonth = useCallback(() => {
    setDisplayMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  }, []);

  // 日付選択ハンドラー
  const handleDateSelect = useCallback((date: Date) => {
    if (onDateSelect) {
      onDateSelect(date);
    }
  }, [onDateSelect]);

  // カレンダーの日付を生成
  const generateCalendarDates = useCallback(() => {
    const year = displayMonth.getFullYear();
    const month = displayMonth.getMonth();
    
    // 月の最初の日と最後の日
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    // 最初の日の曜日（日曜日 = 0）
    const firstDayWeekday = firstDayOfMonth.getDay();
    
    // カレンダーの日付配列
    const dates: Array<{
      date: Date;
      displayDate: number;
      type: DateCellType;
      isSelected: boolean;
    }> = [];

    // 前月の日付を埋める
    for (let i = firstDayWeekday - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      dates.push({
        date,
        displayDate: date.getDate(),
        type: 'otherMonth',
        isSelected: false
      });
    }

    // 当月の日付を埋める
    const today = new Date();
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const date = new Date(year, month, day);
      const isSelected = selectedDate ? 
        (date.getFullYear() === selectedDate.getFullYear() &&
         date.getMonth() === selectedDate.getMonth() &&
         date.getDate() === selectedDate.getDate()) : false;
      
      const isToday = date.getFullYear() === today.getFullYear() &&
                      date.getMonth() === today.getMonth() &&
                      date.getDate() === today.getDate();

      let cellType: DateCellType = 'thisMonth';
      if (isSelected) {
        cellType = 'selected';
      } else if (isToday) {
        cellType = 'today';
      }

      dates.push({
        date,
        displayDate: day,
        type: cellType,
        isSelected
      });
    }

    // 次月の日付を埋める（6週間 = 42日になるまで）
    const remainingCells = 42 - dates.length;
    for (let day = 1; day <= remainingCells; day++) {
      const date = new Date(year, month + 1, day);
      dates.push({
        date,
        displayDate: day,
        type: 'otherMonth',
        isSelected: false
      });
    }

    return dates;
  }, [displayMonth, selectedDate]);

  const calendarDates = generateCalendarDates();
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];

  // 6週間に分割
  const weeks = [];
  for (let i = 0; i < calendarDates.length; i += 7) {
    weeks.push(calendarDates.slice(i, i + 7));
  }

  return (
    <div className="date-picker">
      <div className="date-picker__content">
        {/* ヘッダー */}
        <div className="date-picker__header">
          <IconButton
            icon="chevronLeft"
            onClick={goToPreviousMonth}
            tooltip="前の月"
            tooltipPosition="bottom-right"
            aria-label="前の月"
          />
          
          <h2 className="date-picker__title text-heading-large">
            {getMonthName(displayMonth)}
          </h2>
          
          <IconButton
            icon="chevronRight"
            onClick={goToNextMonth}
            tooltip="次の月"
            tooltipPosition="bottom-left"
            aria-label="次の月"
          />
        </div>

        {/* カレンダー本体 */}
        <div className="date-picker__body">
          {/* 曜日ヘッダー */}
          <div className="date-picker__weekdays">
            {weekdays.map(weekday => (
              <div key={weekday} className="date-picker__weekday text-single-large">
                {weekday}
              </div>
            ))}
          </div>

          {/* 日付グリッド */}
          <div className="date-picker__weeks">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="date-picker__week">
                {week.map((dateInfo, dayIndex) => (
                  <DateCell
                    key={`${weekIndex}-${dayIndex}`}
                    date={dateInfo.date}
                    displayDate={dateInfo.displayDate}
                    type={dateInfo.type}
                    onClick={handleDateSelect}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
